import React, {
  useEffect, useRef, useState, useMemo, useCallback,
} from 'react';
import {
  VStack, HStack, Button, ButtonText, ButtonIcon, Text, Box, Icon, ScrollView, Pressable, Input, InputField, View,
} from '@gluestack-ui/themed';
import {
  Link, Upload, Inbox, Plus,
} from 'lucide-react-native';
import { Platform } from 'react-native';
import { useTranslation } from 'react-i18next';
import type { Resource } from '@/common/api/models/resource';
import type { Task } from '@/common/api/models/task';
import type { ModuleLecturerResponse } from '@/common/api/models/moduleLecturerResponse';
import { useTaskResourceManagement } from '@/common/hooks/lecturer/useTaskResourceManagement';
import { useFilePicker } from '@/common/hooks/lecturer/useFilePicker';
import { useDialog } from '@/common/context/DialogContext';
import { sanitizeFilename } from '@/common/utils/fileUtils';
import ResourceCard from './ResourceCard';

/**
 * Section header component following Flare design hierarchy
 */
const SectionHeader: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Text size="xs" fontWeight="bold" color="$textSubtle" textTransform="uppercase" letterSpacing="$md" mb="$2">
    {children}
  </Text>
);

interface TaskResourceFormProps {
  task: Task;
  module: ModuleLecturerResponse;
  isReadOnly?: boolean;
}

const TaskResourceForm: React.FC<TaskResourceFormProps> = ({
  task,
  module,
  isReadOnly = true,
}) => {
  const moduleId = module?.id;
  const moduleVersion = module?.version;

  const { t } = useTranslation();
  const { showDialog, hideDialog, showToastMessage } = useDialog();
  // mutations for resources
  const {
    createMutation,
    deleteMutation,
    updateMutation,
    cancelUpload,
    isUploading,
    reprocessResource,
    reprocessingId,
    digestPendingUntil,
    moveResourceUp,
    moveResourceDown,
    getSortedResources,
    isUpdating,
  } = useTaskResourceManagement(task);
  const { pickFile } = useFilePicker();

  const [dragCounter, setDragCounter] = useState(0);
  const [oralExamRelevantOverrides, setOralExamRelevantOverrides] = useState<Record<string, boolean>>({});
  const [updatingOralExamRelevantId, setUpdatingOralExamRelevantId] = useState<string | null>(null);
  const isDragging = dragCounter > 0;
  const dropRef = useRef<React.ComponentRef<typeof View>>(null);

  // Sort resources by position
  const sortedResources = useMemo<Resource[]>(() => {
    const resources = (task?.resources || []) as unknown as Resource[];
    return (getSortedResources(resources) || []) as Resource[];
  }, [task?.resources, getSortedResources]);

  const showExamRelevant = task.type !== 'oral_exam' && task.type !== 'event';

  const displayResources = useMemo<Resource[]>(() => sortedResources.map((resource) => {
    if (!(resource.id in oralExamRelevantOverrides)) {
      return resource;
    }
    return { ...resource, oralExamRelevant: oralExamRelevantOverrides[resource.id] };
  }), [sortedResources, oralExamRelevantOverrides]);

  const handleOralExamRelevantChange = useCallback(async (resource: Resource, nextValue: boolean) => {
    if (!task?.id || !resource?.id) return;

    const previousValue = resource.oralExamRelevant !== false;
    setOralExamRelevantOverrides((prev) => ({ ...prev, [resource.id]: nextValue }));
    setUpdatingOralExamRelevantId(resource.id);

    try {
      await updateMutation.mutateAsync({
        moduleId,
        moduleVersion,
        taskId: task.id,
        resourceId: resource.id,
        data: { oralExamRelevant: nextValue },
      });
    } catch (error) {
      setOralExamRelevantOverrides((prev) => ({ ...prev, [resource.id]: previousValue }));
      const errorMessage = (error as { response?: { data?: { detail?: string } } }).response?.data?.detail
        || (error as Error).message
        || 'An unknown error occurred';
      showToastMessage('error', `Error updating resource: ${errorMessage}`);
    } finally {
      setUpdatingOralExamRelevantId(null);
    }
  }, [moduleId, moduleVersion, task?.id, updateMutation, showToastMessage]);

  const uploadFile = useCallback(async (file: File) => {
    if (!task?.id) return;
    try {
      await createMutation.mutateAsync(
        {
          moduleId,
          moduleVersion,
          taskId: task.id,
          data: {
            file,
            title: file.name,
            description: null,
            citation: null,
          },
        },
        {
          onError: (error: Error | { name?: string; response?: { data?: { detail?: string } } }) => {
            if ((error as { name?: string }).name === 'CanceledError' || (error as { name?: string }).name === 'AbortError') {
              showToastMessage('info', 'Upload cancelled');
              return;
            }

            const errorMessage = (error as { response?: { data?: { detail?: string } } }).response?.data?.detail || (error as Error).message || 'An unknown error occurred';
            showToastMessage('error', `Error uploading file: ${errorMessage}`);
          },
        },
      );
    } catch (error) {
      // Errors handled in callback
    }
  }, [moduleId, moduleVersion, task?.id, createMutation, showToastMessage]);

  const handleUploadFile = () => {
    if (isUploading) {
      showToastMessage('info', 'Please wait for the current upload to complete');
      return;
    }
    pickFile(uploadFile);
  };

  const handleAddLink = useCallback(() => {
    if (!task?.id) return;
    let urlValue = '';

    const submitUrl = async () => {
      const urlRegex = /^(https?:\/\/)?([a-z0-9-]+\.)+[a-z]{2,}([/?#].*)?$/i;
      if (!urlValue || !urlRegex.test(urlValue)) {
        showToastMessage('error', 'Please enter a valid web URL (e.g., https://example.com)');
        return;
      }
      hideDialog();
      try {
        await createMutation.mutateAsync({
          moduleId,
          moduleVersion,
          taskId: task.id,
          data: {
            url: urlValue,
            autoGenerateMetadata: true,
          },
        });
      } catch (error) {
        // Error handled by mutation
      }
    };

    showDialog({
      title: 'Add Web Resource',
      message: 'Enter the URL of the web page you want to add as a resource.',
      children: (
        <VStack space="md" p="$4">
          <Input size="md">
            <InputField
              placeholder="https://example.com/article"
              autoFocus
              onChangeText={(text) => { urlValue = text; }}
              onSubmitEditing={submitUrl}
              returnKeyType="done"
            />
          </Input>
          <Text size="xs" color="$textSubtle">
            The system will automatically analyze the page to extract its title, description, and citation.
          </Text>
        </VStack>
      ),
      buttons: [
        {
          text: 'Cancel',
          variant: 'outline',
          action: 'secondary',
          onPress: hideDialog,
        },
        {
          text: 'Add & Analyze',
          action: 'primary',
          onPress: submitUrl,
        },
      ],
    });
  }, [moduleId, moduleVersion, task?.id, createMutation, showDialog, hideDialog, showToastMessage]);

  useEffect(() => {
    if (Platform.OS !== 'web' || !dropRef.current || isReadOnly) {
      return undefined;
    }

    const el = dropRef.current as unknown as HTMLElement;
    if (!el) return undefined;

    const onDragOver = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const onDragEnter = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragCounter((prev) => prev + 1);
    };

    const onDragLeave = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragCounter((prev) => Math.max(0, prev - 1));
    };

    const onDrop = async (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragCounter(0);

      if (e.dataTransfer?.files && e.dataTransfer.files.length > 0) {
        const files = Array.from(e.dataTransfer.files);
        for (const file of files) {
          const sanitizedFile = new File([file], sanitizeFilename(file.name), { type: file.type });
          // eslint-disable-next-line no-await-in-loop
          await uploadFile(sanitizedFile);
        }
      }
    };

    el.addEventListener('dragover', onDragOver);
    el.addEventListener('dragenter', onDragEnter);
    el.addEventListener('dragleave', onDragLeave);
    el.addEventListener('drop', onDrop);

    return () => {
      el.removeEventListener('dragover', onDragOver);
      el.removeEventListener('dragenter', onDragEnter);
      el.removeEventListener('dragleave', onDragLeave);
      el.removeEventListener('drop', onDrop);
    };
  }, [isReadOnly, uploadFile]);

  const handleDelete = (resource: Resource) => {
    if (!task?.id) return;
    showDialog({
      title: 'Delete Resource',
      message: 'Do you really want to delete this resource?',
      buttons: [
        {
          text: 'Cancel', variant: 'outline', action: 'secondary', onPress: hideDialog,
        },
        {
          text: 'Delete',
          action: 'negative',
          onPress: () => {
            deleteMutation.mutate({
              moduleId,
              moduleVersion,
              taskId: task.id,
              resourceId: resource.id,
            });
          },
        },
      ],
    });
  };

  const handleOpen = async (item: Resource) => {
    try {
      if (Platform.OS === 'web') {
        if (item.type === 'link') {
          const { url } = item;
          if (!url) throw new Error('Resource URL not available');
          const finalUrl = url.startsWith('http') ? url : `http://${url}`;
          window.open(finalUrl, '_blank', 'noopener,noreferrer');
        } else if (item.url) {
          window.open(item.url, '_blank', 'noopener,noreferrer');
        } else {
          throw new Error('Resource URL not available');
        }
      }
    } catch (error) {
      const errorMessage = (error as Error).message || 'An unknown error occurred';
      showToastMessage('error', `Failed to open resource: ${errorMessage}`);
    }
  };

  const handleMoveUp = (resourceId: string) => moveResourceUp(resourceId, moduleId, moduleVersion);
  const handleMoveDown = (resourceId: string) => moveResourceDown(resourceId, moduleId, moduleVersion);

  const handleRetryDigest = (resource: Resource) => {
    if (!resource?.id) return;
    reprocessResource(resource.id, moduleId, moduleVersion)
      .then(() => showToastMessage('info', t('lecturer.resourceDigest.retryStarted')))
      .catch(() => showToastMessage('error', t('lecturer.resourceDigest.retryError')));
  };

  const canMoveUp = (resourceId: string) => {
    const currentIndex = sortedResources.findIndex((r) => r.id === resourceId);
    return currentIndex > 0 && !isUpdating;
  };

  const canMoveDown = (resourceId: string) => {
    const currentIndex = sortedResources.findIndex((r) => r.id === resourceId);
    return currentIndex < sortedResources.length - 1 && !isUpdating;
  };

  return (
    <Box flex={1} bg="$bgApp" ref={dropRef}>
      <ScrollView flex={1} bg="$bgApp">
        <VStack flexGrow={1} space="xl" px="$6" py="$4" testID="resource-list">
          {/* Header Section */}
          <HStack alignItems="center" justifyContent="space-between">
            <SectionHeader>Resources</SectionHeader>

            {!isReadOnly && (
              <HStack space="md">
                <Button
                  size="sm"
                  action="secondary"
                  testID="add-link-button"
                  onPress={handleAddLink}
                >
                  <ButtonIcon as={Link} size="xs" mr="$1" color="$textHeading" />
                  <ButtonText>Add Link</ButtonText>
                </Button>

                <Button
                  size="sm"
                  testID="upload-file-button"
                  action="secondary"
                  onPress={handleUploadFile}
                  isDisabled={isUploading}
                  opacity={isUploading ? 0.6 : 1}
                >
                  <ButtonIcon as={Upload} size="xs" mr="$1" color="$textHeading" />
                  <ButtonText>{isUploading ? 'Uploading...' : 'Upload File'}</ButtonText>
                </Button>
              </HStack>
            )}
          </HStack>

          {/* Resource List */}
          {sortedResources.length > 0 && (
            <VStack space="md">
              {displayResources.map((item) => (
                <ResourceCard
                  key={item.id}
                  resource={item}
                  onOpen={() => handleOpen(item)}
                  onDelete={() => handleDelete(item)}
                  onChange={(resourceData: Resource) => updateMutation.mutate({
                    moduleId,
                    moduleVersion,
                    taskId: task.id,
                    resourceId: resourceData.id,
                    data: resourceData,
                  })}
                  onCancelUpload={cancelUpload}
                  onRetryDigest={() => handleRetryDigest(item)}
                  isRetryingDigest={reprocessingId === item.id}
                  digestPendingUntil={digestPendingUntil?.[item.id]}
                  onMoveUp={() => handleMoveUp(item.id)}
                  onMoveDown={() => handleMoveDown(item.id)}
                  canMoveUp={canMoveUp(item.id)}
                  canMoveDown={canMoveDown(item.id)}
                  readonly={isReadOnly}
                  showExamRelevant={showExamRelevant}
                  onOralExamRelevantChange={(value) => handleOralExamRelevantChange(item, value)}
                  isOralExamRelevantUpdating={updatingOralExamRelevantId === item.id}
                />
              ))}
            </VStack>
          )}

          {/* Interactive Drop Zone - Placed at the bottom */}
          {!isReadOnly && (
            <Pressable onPress={handleUploadFile}>
              <Box
                borderWidth={2}
                borderStyle="dashed"
                borderColor={isDragging ? '$brandBase' : '$borderLight'}
                bg={isDragging ? '$bgBrandSubtle' : '$bgSubtle'}
                borderRadius="$lg"
                py="$12"
                px="$8"
                minHeight={200}
                alignItems="center"
                justifyContent="center"
                sx={{
                  _web: {
                    cursor: 'pointer',
                    transition: 'all 0.2s ease-in-out',
                    ':hover': {
                      borderColor: '$borderBrand',
                      bg: '$bgHover',
                    },
                  },
                }}
              >
                <VStack space="md" alignItems="center" pointerEvents="none">
                  <Box
                    bg={isDragging ? '$brandSubtle' : '$neutral100'}
                    p="$4"
                    borderRadius="$full"
                    sx={{
                      _web: {
                        transition: 'transform 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                        transform: isDragging ? 'scale(1.1) translateY(4px)' : 'scale(1)',
                      },
                    }}
                  >
                    <Icon
                      as={Inbox}
                      size="xl"
                      color={isDragging ? '$brandHover' : '$textSubtle'}
                    />
                  </Box>
                  <VStack space="xs" alignItems="center">
                    <HStack space="xs" alignItems="center">
                      <Icon as={Plus} size="xs" color="$textHeading" />
                      <Text size="md" color="$textHeading" fontWeight="semibold">
                        Drop files here
                      </Text>
                    </HStack>
                    <Text size="sm" color="$textSubtle" textAlign="center">
                      or click to browse your computer
                    </Text>
                  </VStack>
                </VStack>
              </Box>
            </Pressable>
          )}

          {/* Empty State - Only show when Read Only and no resources */}
          {isReadOnly && sortedResources.length === 0 && (
            <Box py="$12" alignItems="center" justifyContent="center">
              <Icon as={Inbox} size="xl" color="$textHint" mb="$2" />
              <Text size="sm" color="$textSubtle">
                No resources added yet
              </Text>
            </Box>
          )}
        </VStack>
      </ScrollView>
    </Box>
  );
};

export default TaskResourceForm;
